import { createContext, useContext, useEffect, useState } from "react";
import api from "../lib/api";
import { isPublicPath } from "../lib/publicPaths";

const AuthCtx = createContext({ user: undefined, login: async () => {}, logout: async () => {} });

export function AuthProvider({ children }) {
  const [user, setUser] = useState(undefined); // undefined = checking, null = unauth, obj = auth

  useEffect(() => {
    // Skip auth probing on public pages — they should never need to know about user state
    // and a stale cookie's 401 response (even with skipAuthRedirect) adds latency/console noise.
    if (typeof window !== "undefined" && isPublicPath(window.location.pathname)) {
      setUser(null);
      return;
    }
    api.get("/auth/me", { skipAuthRedirect: true })
      .then((r) => setUser(r.data))
      .catch(() => setUser(null));
  }, []);

  async function login(email, password) {
    const { data } = await api.post("/auth/login", { email, password });
    setUser(data);
    return data;
  }

  async function register(name, email, password, role = "customer", extras = {}) {
    const { data } = await api.post("/auth/register", { name, email, password, role, ...extras });
    setUser(data);
    return data;
  }

  async function otpLogin(phone, code, name) {
    const { data } = await api.post("/customer-auth/otp/verify", { phone, code, name });
    setUser(data);
    return data;
  }

  async function logout() {
    try { await api.post("/auth/logout"); } catch (e) { /* ignore */ }
    setUser(null);
  }

  async function refresh() {
    try {
      const { data } = await api.get("/auth/me", { skipAuthRedirect: true });
      setUser(data);
      return data;
    } catch (e) {
      setUser(null);
      return null;
    }
  }

  return (
    <AuthCtx.Provider value={{ user, login, register, otpLogin, logout, refresh }}>{children}</AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
